import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { identity, noop, takeUntil } from "rxjs";
import dayjs from "@gtmhub/dayjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { MetricTargetOperator } from "../../models/metric.models";
import { MetricMilestoneValidators } from "./metric-milestone-validator";
import { Milestone, MilestoneFormArray, MilestoneFormControls, MilestoneFormGroup } from "./metric-milestone.models";

@Component({
  selector: "metric-milestone",
  templateUrl: "./metric-milestone.component.html",
  styleUrls: ["./metric-milestone.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MetricMilestoneComponent,
      multi: true,
    },
  ],
})
export class MetricMilestoneComponent implements OnInit, OnChanges, ControlValueAccessor {
  public form: FormGroup<MilestoneFormGroup>;
  public onChange = identity;
  public onTouch = noop;
  public errorTranslationKey: string;
  public defaultDate: Date;
  public addMilestoneBtnIsClicked = false;

  @Input() public disabled: boolean;
  @Input() public selected: Milestone[];
  @Input() private targetNumber: number;
  @Input() private targetOperator: MetricTargetOperator;
  @Input() public minStartDate: Date;
  @Input() public maxEndDate: Date;
  @Input() public hardDeadline: Date;

  @Output()
  public readonly selectedChange = new EventEmitter<Milestone[]>();

  constructor(private fb: FormBuilder, private destroyService: DestroyService) {}

  public ngOnInit(): void {
    if (this.selected) {
      this.form = this.fb.group<MilestoneFormGroup>({
        milestones: this.fb.array<FormGroup<MilestoneFormControls>>(this.selected.map((milestone) => this.createMilestoneFormGroup(milestone))),
      });
    } else {
      this.form = this.fb.group<MilestoneFormGroup>({
        milestones: this.fb.array<FormGroup<MilestoneFormControls>>([]),
      });
    }

    this.subscribeToValueChange();

    if (this.checkIfDateIsDisabled(new Date())) {
      this.defaultDate = new Date(this.minStartDate);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.targetNumber && !changes.targetNumber.isFirstChange()) {
      this.updateFormControlsValidationOnChange();
    }

    if (changes.targetOperator && !changes.targetOperator.isFirstChange()) {
      this.updateFormControlsValidationOnChange();
    }
  }

  private updateFormControlsValidationOnChange(): void {
    this.milestones.controls.forEach((control: FormGroup<MilestoneFormControls>) => {
      control.get("targetValue").setValidators([MetricMilestoneValidators.range(this.targetNumber, this.targetOperator)]);
      control.get("targetValue").updateValueAndValidity();
    });
  }

  public writeValue(obj: Milestone[]): void {
    this.setValue(obj);
  }

  public registerOnChange(fn: typeof identity): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: typeof noop): void {
    this.onTouch = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
      return;
    }

    this.form.enable();
  }

  public get milestones(): MilestoneFormArray {
    return this.form.controls.milestones;
  }

  public addMilestone(): void {
    this.milestones.push(this.createEmptyMilestoneFormGroup());
    this.addMilestoneBtnIsClicked = true;
  }

  public removeMilestone(index: number): void {
    if (this.milestones.length === 0) {
      return;
    }

    this.milestones.removeAt(index);
  }

  public checkIfDateIsDisabled = (date: Date): boolean => {
    const dueDate = dayjs(date).startOf("day");

    const minDateCheck = this.minStartDate ? dueDate.isBefore(dayjs(this.minStartDate).startOf("day")) : false;

    const maxDateCheck = this.maxEndDate ? dueDate.isAfter(dayjs(this.maxEndDate).endOf("day")) : false;

    const hardDeadlineCheck = this.hardDeadline ? dueDate.isAfter(dayjs(this.hardDeadline).subtract(1, "day").endOf("day")) : false;

    const milestoneDateCheck = this.milestones.controls.some((control) => {
      const milestoneDate = control.get("date").value;
      return dueDate.isSame(dayjs(milestoneDate).startOf("day"));
    });

    return minDateCheck || maxDateCheck || hardDeadlineCheck || milestoneDateCheck;
  };

  private setValue(milestones: Milestone[], options: { emitEvent: boolean } = { emitEvent: false }): void {
    this.form.patchValue({ milestones: milestones }, options);
  }

  private sortMilestonesDesc(): (a: Milestone, b: Milestone) => number {
    return (objA, objB) => Number(objB.date) - Number(objA.date);
  }

  private createEmptyMilestoneFormGroup(): FormGroup<MilestoneFormControls> {
    return this.fb.group<MilestoneFormControls>({
      targetValue: this.fb.control(null, [MetricMilestoneValidators.range(this.targetNumber, this.targetOperator)]),
      date: this.fb.control(null),
    });
  }

  private createMilestoneFormGroup(selectedMilestone: Milestone): FormGroup<MilestoneFormControls> {
    return this.fb.group<MilestoneFormControls>({
      targetValue: this.fb.control(selectedMilestone.targetValue, [MetricMilestoneValidators.range(this.targetNumber, this.targetOperator)]),
      date: this.fb.control(new Date(selectedMilestone.date)),
    });
  }

  private subscribeToValueChange(): void {
    this.milestones.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((arr: Milestone[]) => {
      const sortedDesc = arr.slice().sort(this.sortMilestonesDesc());

      this.setValue(sortedDesc);
      const lastMilestone = this.milestones.controls[0]?.controls.targetValue;
      this.errorTranslationKey = lastMilestone?.errors?.translate_key;
      this.selectedChange.emit(sortedDesc);
    });
  }
}
